import ExcelJS from 'exceljs';

export interface ExcelColDef {
  header: string;
  key: string;
  width: number;
  numFmt?: string;
  align?: 'left' | 'center' | 'right';
}

export interface ExcelRowDef {
  data: Record<string, string | number>;
  /** 0 = director/section header, 1 = normal row, 2 = sub-row */
  level?: 0 | 1 | 2;
  isSection?: boolean;
}

const HEADER_BG  = 'FF1E2A4A';
const HEADER_FG  = 'FFFFFFFF';
const SECTION_BG = 'FFE0E7FF'; // indigo-100
const SECTION_FG = 'FF3730A3';
const LVL0_BG    = 'FFF1F5F9'; // slate-100
const LVL1_BG    = 'FFFFFFFF';
const LVL2_BG    = 'FFFAFAFA';
const BORDER_CLR = 'FFE2E8F0';
const TEXT_DARK  = 'FF1E293B';

export const CURRENCY_FMT = '#,##0.00';
export const PCT_FMT      = '0.0"%"';

function addStyledSheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  columns: ExcelColDef[],
  rows: ExcelRowDef[],
): void {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.columns = columns.map(c => ({ key: c.key, width: c.width }));

  // ── Header row ──
  const hdr = ws.addRow(columns.map(c => c.header));
  hdr.height = 28;
  hdr.eachCell((cell, i) => {
    cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.font   = { bold: true, size: 10, color: { argb: HEADER_FG }, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: columns[i - 1]?.align ?? 'left', wrapText: false };
    cell.border = {
      top:    { style: 'thin', color: { argb: HEADER_BG } },
      bottom: { style: 'medium', color: { argb: 'FF4F5FA3' } },
      left:   { style: 'thin', color: { argb: HEADER_BG } },
      right:  { style: 'thin', color: { argb: HEADER_BG } },
    };
  });

  // ── Data rows ──
  rows.forEach(({ data, level = 1, isSection }) => {
    const row = ws.addRow(columns.map(c => data[c.key] ?? ''));
    row.height = isSection ? 22 : 20;

    const bg = isSection ? SECTION_BG : level === 0 ? LVL0_BG : level === 2 ? LVL2_BG : LVL1_BG;

    row.eachCell((cell, i) => {
      const col = columns[i - 1];
      if (col?.numFmt) cell.numFmt = col.numFmt;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      cell.font = {
        bold:   level === 0 || isSection,
        italic: isSection,
        size:   10,
        name:   'Calibri',
        color:  { argb: isSection ? SECTION_FG : TEXT_DARK },
      };
      cell.alignment = { vertical: 'middle', horizontal: col?.align ?? 'left' };
      cell.border    = { bottom: { style: 'thin', color: { argb: BORDER_CLR } } };
    });
  });
}

async function writeAndDownload(wb: ExcelJS.Workbook, filename: string): Promise<void> {
  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url    = URL.createObjectURL(blob);
  const link   = document.createElement('a');
  link.href    = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function downloadStyledExcel(
  filename: string,
  sheetName: string,
  columns: ExcelColDef[],
  rows: ExcelRowDef[],
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'K-PROD';
  wb.created = new Date();
  addStyledSheet(wb, sheetName, columns, rows);
  await writeAndDownload(wb, filename);
}

export interface ExcelSheetDef {
  name: string;
  columns: ExcelColDef[];
  rows: ExcelRowDef[];
}

export async function downloadMultiSheetExcel(
  filename: string,
  sheets: ExcelSheetDef[],
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'K-PROD';
  wb.created = new Date();
  sheets.forEach(s => addStyledSheet(wb, s.name, s.columns, s.rows));
  await writeAndDownload(wb, filename);
}
